import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cellMetricsService } from '../../../services/cellMetricsService';
import { processCellMetricsData } from '../../../utils/cellDataProcessor';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';

const metricOptions = [
  { value: 'cellMilliVolt', label: 'Cell Voltage (V)' },
  { value: 'cellTempC', label: 'Cell Temp (°C)' }
];

const CellMetrics = ({ emsName }) => {
  const [selectedMetric, setSelectedMetric] = useState('cellMilliVolt');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });

  // Local pickers for "from" and "to"
  const [fromPicker, setFromPicker] = useState(fromDate);
  const [toPicker, setToPicker] = useState(toDate);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedSlaveId, setSelectedSlaveId] = useState(1); // Default to 1 instead of null
  const [selectedCellIndices, setSelectedCellIndices] = useState([1]); // Array for multi-select
  
  // Extract unique slave IDs and cell indices from data
  const slaveIds = React.useMemo(() => {
    if (!data.length) return [];
    const uniqueSlaves = [...new Set(data.map(row => row.slaveId))];
    console.log('Available slave IDs:', uniqueSlaves);
    return uniqueSlaves.sort((a, b) => a - b);
  }, [data]);

  const cellIndices = React.useMemo(() => {
    if (!data.length) return [];
    const uniqueCells = [...new Set(data
      .filter(row => row.slaveId === selectedSlaveId)
      .map(row => row.cellIndex))];
    console.log('Available cell indices:', uniqueCells);
    return uniqueCells.sort((a, b) => a - b);
  }, [data, selectedSlaveId]);

  // Filter data based on selections - remove null checks
  const filteredData = React.useMemo(() => {
    console.log('Filtering with:', { selectedSlaveId, selectedCellIndices });
    return data.filter(row => 
      row.slaveId === selectedSlaveId &&
      selectedCellIndices.includes(row.cellIndex)
    );
  }, [data, selectedSlaveId, selectedCellIndices]);

  const fetchData = useCallback(async (from, to) => {
    if (!emsName) return;
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching data with:', { emsName, from, to });
      // Send UTC timestamps to backend
      const formatUTC = date => date.toISOString().slice(0, 19).replace('T', ' ');
      
      const response = await cellMetricsService.getCellMetrics(
        emsName,
        formatUTC(from),
        formatUTC(to)
      );
      
      const processedData = processCellMetricsData(response.data || []);
      setData(processedData);

      // Initialize selectors with first available values
      if (processedData.length > 0) {
        const uniqueSlaves = [...new Set(processedData.map(row => row.slaveId))].sort((a, b) => a - b);
        if (uniqueSlaves.length > 0) {
          setSelectedSlaveId(uniqueSlaves[0]);
          const cellsForSlave = [...new Set(processedData
            .filter(row => row.slaveId === uniqueSlaves[0])
            .map(row => row.cellIndex))].sort((a, b) => a - b);
          if (cellsForSlave.length > 0) {
            setSelectedCellIndices(cellsForSlave);
          }
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load cell metrics');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [emsName]);

  const formatXAxis = useCallback((timestamp) => {
    // Display in local timezone
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  }, []);

  // Group data by cellIndex for plotting multiple lines
  const cellDataMap = React.useMemo(() => {
    const map = {};
    selectedCellIndices.forEach(idx => {
      map[idx] = filteredData.filter(row => row.cellIndex === idx);
    });
    return map;
  }, [filteredData, selectedCellIndices]);

  const renderChart = useCallback(() => {
    if (!filteredData.length) return null;
    return (
      <Box sx={{ width: 900, height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart>
            <CartesianGrid strokeDasharray="10 10" />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={['auto', 'auto']}
              scale="time"
              tickFormatter={formatXAxis}
            />
            <YAxis domain={['auto', 'auto']} />
            <Tooltip
              labelFormatter={(label) => new Date(label).toLocaleString()}
              formatter={(value, name) => {
                const unit = selectedMetric === 'cellMilliVolt' ? 'V'
                  : selectedMetric === 'cellTempC' ? '°C'
                  : '';
                return [value, unit];
              }}
            />
            <Legend />
            {selectedCellIndices.map((cellIdx, i) => (
              <Line
                key={cellIdx}
                type="monotone"
                data={cellDataMap[cellIdx]}
                dataKey={selectedMetric}
                name={`Cell ${cellIdx}`}
                stroke={`hsl(${(i * 60) % 360}, 70%, 50%)`}
                dot={false}
                isAnimationActive={false}
                strokeWidth={1}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  }, [filteredData, selectedMetric, formatXAxis, selectedCellIndices, cellDataMap]);

  const handleCellIndexChange = (event) => {
    const value = event.target.value;
    setSelectedCellIndices(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  return (
    <Box sx={{ width: 'fit-content' }}>
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        justifyContent: 'flex-start',
        '& .MuiFormControl-root': {
          minWidth: 180
        }
      }}>
        {/* Only show selectors when data is available */}
        {data.length > 0 && (
          <>
            <FormControl size="small">
              <InputLabel>Slave ID</InputLabel>
              <Select
                value={selectedSlaveId}
                label="Slave ID"
                onChange={(e) => {
                  const value = Number(e.target.value);
                  console.log('Selected slave ID:', value);
                  setSelectedSlaveId(value);
                  // Reset to all cells for new slave
                  const cellsForSlave = cellIndices;
                  setSelectedCellIndices(cellsForSlave.length ? [cellsForSlave[0]] : []);
                }}
              >
                
                {slaveIds.map(id => (
                  <MenuItem style={{ display: 'flex', flexDirection: 'column',padding: '8px ' }} key={id} value={id}>Slave {id}</MenuItem>
                ))}
              
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Cell Index</InputLabel>
              <Select
                multiple
                value={selectedCellIndices}
                onChange={handleCellIndexChange}
                renderValue={(selected) => selected.map(idx => `Cell ${idx}`).join(', ')}
                label="Cell Index"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 200,
                    },
                  },
                }}
              >
                {cellIndices.map(index => (
                  <MenuItem key={index} value={index}>
                    <Checkbox checked={selectedCellIndices.indexOf(index) > -1} />
                    <ListItemText primary={`Cell ${index}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Metric</InputLabel>
              <Select
                value={selectedMetric}
                label="Metric"
                onChange={(e) => setSelectedMetric(e.target.value)}
                MenuProps={{
                    sx: {
                      '& .MuiMenu-paper': {
                        '& .MuiMenuItem-root': {
                          padding: '8px 16px',
                          margin: '4px 0',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }
                      }
                    }
                  }}
              >
                {metricOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateTimePicker
              label="From"
              value={fromPicker}
              onChange={(date) => date && setFromPicker(date)}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true
                }
              }}
              ampm={false}
            />
          </Box>
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateTimePicker
              label="To"
              value={toPicker}
              onChange={(date) => date && setToPicker(date)}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true
                }
              }}
              ampm={false}
            />
          </Box>
        </LocalizationProvider>
        <Button
          variant="contained"
          size="medium"
          onClick={() => {
            setFromDate(fromPicker);
            setToDate(toPicker);
            console.log('Selected From:', fromPicker, 'Selected To:', toPicker); // print selected dates
            fetchData(fromPicker, toPicker);
          }}
        >
          Query
        </Button>
      </Box>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        filteredData.length > 0 ? (
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              {metricOptions.find(opt => opt.value === selectedMetric)?.label} Metrics
              {selectedSlaveId && ` - Slave ${selectedSlaveId}`}
              {selectedCellIndices.length > 0 && ` - Cells ${selectedCellIndices.join(', ')}`}
            </Typography>
            {renderChart()}
          </Box>
        ) : (
          <Alert severity="info">No data available for the selected period</Alert>
        )
      )}
    </Box>
  );
};

export default React.memo(CellMetrics);